import { useEffect, useState } from 'react';
import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { RequestStatus } from '../../../../../../common/enums/requestStatus';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { RequestReadButtons } from '../../../../elements/carePathButtons/RequestReadButtons';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { ProfessionalSummaryDataRead } from '../../../professionalSummaryData/ProfessionalSummaryDataRead';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { Icd10SelectionsRead } from '../../utils/v1/Icd10/Icd10SelectionsRead';
import { MedicationsRead } from '../../utils/v1/medications/MedicationsRead';
import { ProfileImages } from '../../utils/v1/ProfileImages';
import { ServiceConsumerSupervisorRead } from '../../utils/v1/ServiceConsumerSupervisorRead';
import { TxcsMedicationsList } from '../../utils/v1/TxcsMedicationsList';
import { SpirometryRead } from './SpirometryRead';
import { TpcRequestFollowingSteps } from './TpcRequestFollowingSteps';
import { TpcV1RequestSummary } from './TpcV1RequestSummary';

export function TpcV1RequestRead(props: ICustomControlRequestReadProps) {
    const [isFooterVisible, setIsFooterVisible] = useState(false);

    useEffect(() => {
        checkIsFooterVisible();
    }, [props.request.customControlData, props.request.status]);

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={
                    props.process.requesterOrganization?.name ? props.process.requesterOrganization?.name : undefined
                }
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            <TpcV1RequestSummary {...props} />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                {props.request.customControlData && (
                    <SpirometryRead
                        customControlData={props.request.customControlData}
                        pulmoType="request"
                    />
                )}

                <MedicationsRead
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    onContentLoaded={async (hasContent) => setIsFooterVisible(hasContent)}
                    patientUsesMedication={props.request.patientUsesMedication}
                />
                <ProfessionalSummaryDataRead request={props.request} />
                <TxcsMedicationsList
                    onContentLoaded={async (hasContent) => {
                        if (hasContent) {
                            setIsFooterVisible(true);
                        }
                    }}
                    requestId={props.request.id}
                />

                <Icd10SelectionsRead
                    diagnosis={props.request.diagnosis}
                    diagnosisConclusion={props.request.customControlData?.conclusie_diagnose}
                    hideTitle
                    thesaurusTermDate={props.request.dateSent}
                />

                <ProfileImages request={props.request} />
                <ServiceConsumerSupervisorRead {...props} />

                <TpcRequestFollowingSteps />
            </CustomControlCollapseBody>

            {isFooterVisible && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('request.hideConsult')}
                    labelMore={translateKey('request.showConsult')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
            <RequestReadButtons {...props} />
        </ProcessContainer>
    );

    function checkIsFooterVisible() {
        const customControlData = props.request.customControlData;
        if (
            (customControlData &&
                ((customControlData.gewicht !== null && customControlData.lengte !== null) ||
                    customControlData.ACQ ||
                    customControlData.CCQ ||
                    customControlData.MRC ||
                    customControlData.klachten)) ||
            props.request.diagnosis?.diagnosisDifferentialConsiderationIcd10a ||
            props.request.status === RequestStatus.requested ||
            props.request.supervisorPractitionerId
        ) {
            setIsFooterVisible(true);
        } else {
            setIsFooterVisible(false);
        }
    }
}
